import { Injectable, WritableSignal, computed, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  EmployeeClient,
  EmployeeV3ForCardDto,
  EmployeeV3ForCardResponseDto,
  EmployeeV3PositionItemDto,
  IcLookupTypeEnumDto,
} from '@data-access/bulk-operations-api';
import { WebSocketClient, WsServerPayloadEnum } from '@data-access/bulk-operations-ws';
import { getActiveTimelineItem } from '@em-helpers';
import { Observable, Subject, filter, retry } from 'rxjs';
import { switchMap, take, tap } from 'rxjs/operators';

import { EnumEmployeeCardState } from './employee-card-state.enum';

@Injectable({
  providedIn: 'root',
})
export class SelectedEmployeeService {
  private employeeClient = inject(EmployeeClient);
  private webSocketClient = inject(WebSocketClient);

  private readonly _employeeIdSignal: WritableSignal<string> = signal(undefined);
  private readonly _positionIdSignal: WritableSignal<string> = signal(undefined);
  private readonly _employeeNumberSignal: WritableSignal<string> = signal(undefined);
  private readonly _empmanPositionIndexSignal: WritableSignal<number> = signal(undefined);
  private readonly _employeeLegacyIdSignal: WritableSignal<string> = signal(undefined);

  public readonly employeeIdSignal = this._employeeIdSignal.asReadonly();
  public readonly positionIdSignal = this._positionIdSignal.asReadonly();
  public readonly employeeNumberSignal = this._employeeNumberSignal.asReadonly();
  public readonly empmanPositionIndexSignal = this._empmanPositionIndexSignal.asReadonly();
  public readonly employeeLegacyIdSignal = this._employeeLegacyIdSignal.asReadonly();

  private readonly _employeeSignal: WritableSignal<EmployeeV3ForCardDto> = signal(undefined);
  private readonly employee$ = toObservable(this._employeeSignal);
  private readonly _positionSignal: WritableSignal<EmployeeV3PositionItemDto> = signal(undefined);
  private readonly position$ = toObservable(this._positionSignal);
  private readonly _stateSignal = signal<EnumEmployeeCardState>(EnumEmployeeCardState.Loading);
  public readonly stateSignal = this._stateSignal.asReadonly();

  public readonly employeeSignal = this._employeeSignal.asReadonly();
  public readonly positionSignal = this._positionSignal.asReadonly();

  public readonly employeeNameSignal = computed(() => {
    const employee = this._employeeSignal();
    return `${employee?.firstName || ''} ${employee?.lastName || ''}`;
  });

  // Used to cancel old request when calling in sequence.
  private readonly fetchEmployeeSubject = new Subject<string>();

  constructor() {
    this.subscribeToFetchEmployee();
    this.subscribeToWebSocket();
  }

  /*
   * @deprecated This method is deprecated. Use signal instead.
   */
  public getEmployee(): Observable<EmployeeV3ForCardDto> {
    return this.employee$.pipe(filter((employee) => !!employee));
  }

  /*
   * @deprecated This method is deprecated. Use signal instead.
   */
  public getPosition(): Observable<EmployeeV3PositionItemDto> {
    return this.position$.pipe(filter((position) => !!position));
  }

  public setActivePosition(positionId: string): void {
    this._stateSignal.set(EnumEmployeeCardState.Loading);
    const employee = this._employeeSignal();
    const positionIndex = employee.positionTimeline.findIndex(
      (item) => item.value.id === positionId,
    );
    const position = employee.positionTimeline[positionIndex];

    if (position) {
      this._positionIdSignal.set(positionId);
      this._positionSignal.set(position);
      // EmpMan position index is 1-based
      this._empmanPositionIndexSignal.set(positionIndex + 1);
    }
    this._stateSignal.set(EnumEmployeeCardState.Ready);
  }

  public fetchEmployee(employeeId: string): void {
    this.fetchEmployeeSubject.next(employeeId);
  }

  public cleanupEmployee(): void {
    this._employeeIdSignal.set(undefined);
    this._positionIdSignal.set(undefined);
    this._employeeNumberSignal.set(undefined);
    this._empmanPositionIndexSignal.set(undefined);
    this._employeeSignal.set(undefined);
    this._positionSignal.set(undefined);
  }

  private subscribeToWebSocket() {
    this.webSocketClient
      .getMessagesByType(WsServerPayloadEnum.IcUpserted)
      .pipe(filter((msg) => msg.type === IcLookupTypeEnumDto.Employee))
      .subscribe((msg) => {
        const icUpsertedEmployeeIds = [
          ...msg.employeeItems.map((item) => item.id),
          ...msg.positionItems.map((item) => item.employeeId),
        ];

        if (
          !this._employeeIdSignal() ||
          !icUpsertedEmployeeIds.includes(this._employeeIdSignal())
        ) {
          return;
        }

        this.fetchEmployee(this._employeeIdSignal());
      });

    this.webSocketClient
      .getMessagesByType(WsServerPayloadEnum.EmployeeCardEditActionCompleted)
      .pipe(filter((msg) => !!msg.errorMessage))
      .subscribe((msg) => {
        if (!this._employeeIdSignal() || this._employeeIdSignal() !== msg.employeeId) {
          return;
        }

        this.fetchEmployee(this._employeeIdSignal());
      });
  }

  private subscribeToFetchEmployee(): void {
    this.fetchEmployeeSubject
      .pipe(
        tap(() => this._stateSignal.set(EnumEmployeeCardState.Loading)),
        switchMap((employeeId) =>
          this.employeeClient.getEmployee(employeeId).pipe(retry(3), take(1)),
        ),
      )
      .subscribe({
        next: this.onNextGetEmployeeV3.bind(this),
        error: this.onErrorGetEmployeeV3.bind(this),
      });
  }

  private onNextGetEmployeeV3(result: EmployeeV3ForCardResponseDto) {
    this._employeeSignal.set(result.employee);
    this._employeeIdSignal.set(result.employee.id);
    this._employeeNumberSignal.set(result.employee.customId);
    this._employeeLegacyIdSignal.set(result.employee.legacyId);

    if (result.employee.positionTimeline.length === 0) {
      this._positionSignal.set(undefined);
      this._positionIdSignal.set(undefined);
      this._empmanPositionIndexSignal.set(undefined);
      this._stateSignal.set(EnumEmployeeCardState.Ready);
      return;
    }

    let positionIndex = result.employee.positionTimeline.findIndex(
      (item) => item === getActiveTimelineItem(result.employee.positionTimeline),
    );

    if (positionIndex === -1) {
      positionIndex = result.employee.positionTimeline.length - 1;
    }

    const position = result.employee.positionTimeline[positionIndex];

    this._positionSignal.set(position);
    this._positionIdSignal.set(position.value.id);
    // EmpMan position index is 1-based
    this._empmanPositionIndexSignal.set(positionIndex + 1);

    this._stateSignal.set(EnumEmployeeCardState.Ready);
  }

  private onErrorGetEmployeeV3(err: unknown) {
    this._stateSignal.set(EnumEmployeeCardState.Error);
    throw err;
  }
}
